import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Alert, Box, Button, Chip, IconButton, List, ListItemButton, ListItemText, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { request, useOperations } from './OperationsProvider';
import { loadGoogle } from './GoogleOperationsMap';
import { AREA_COLOUR, areaTypeLabel } from './areas';

// Dropping a pin gives a point, not an extent, so the circle starts at a size that
// covers a typical plant yard and the administrator drags it to fit.
const DEFAULT_RADIUS = 300;
// Equirectangular distance; accurate far beyond the scale of a single site.
const metres = (lat1, lon1, lat2, lon2) => {
  const a = (lat1 * Math.PI) / 180; const b = (lat2 * Math.PI) / 180;
  const x = (((lon2 - lon1) * Math.PI) / 180) * Math.cos((a + b) / 2);
  return 6371000 * Math.sqrt(x * x + (b - a) * (b - a));
};

const TYPES = [
  ['PLANT', 'Plant area', 'Checkpoint and trip logic applies inside this boundary.'],
  ['PARKING', 'Outside parking', 'Vehicles standing here are waiting to be called in.'],
  // A warehouse is neither of the above, and marking it as either is actively wrong: as a plant it
  // would report every truck at its own dock as being on site without a trip, and as parking it
  // would report each one as waiting to be called in. Nothing is expected of a vehicle for being
  // here, which is what lets a fleet run between sites without answering for gate paperwork.
  ['WAREHOUSE', 'Warehouse', 'A site trucks run between. No gate rules apply inside it.'],
];

const area2 = (ring) => Math.abs(ring.reduce((sum, p, i) => {
  const q = ring[(i + 1) % ring.length];
  return sum + (p.lon * q.lat - q.lon * p.lat);
}, 0));

const contains = (ring, lat, lon) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const yi = ring[i].lat; const xi = ring[i].lon; const yj = ring[j].lat; const xj = ring[j].lon;
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

// Ramer–Douglas–Peucker. A raw OSM boundary can carry hundreds of vertices, which is
// accurate but impossible to nudge by hand; thinning to a few dozen keeps the shape and
// makes "minor adjustment" actually possible.
const simplify = (ring, tolerance) => {
  if (ring.length <= 3) return ring;
  const dist = (p, a, b) => {
    const dx = b.lon - a.lon; const dy = b.lat - a.lat;
    if (dx === 0 && dy === 0) return Math.hypot(p.lon - a.lon, p.lat - a.lat);
    const t = Math.max(0, Math.min(1, ((p.lon - a.lon) * dx + (p.lat - a.lat) * dy) / (dx * dx + dy * dy)));
    return Math.hypot(p.lon - (a.lon + t * dx), p.lat - (a.lat + t * dy));
  };
  const run = (pts) => {
    if (pts.length < 3) return pts;
    let index = 0; let max = 0;
    for (let i = 1; i < pts.length - 1; i += 1) {
      const d = dist(pts[i], pts[0], pts[pts.length - 1]);
      if (d > max) { max = d; index = i; }
    }
    if (max <= tolerance) return [pts[0], pts[pts.length - 1]];
    return [...run(pts.slice(0, index + 1)).slice(0, -1), ...run(pts.slice(index))];
  };
  const out = run([...ring, ring[0]]);
  return out.slice(0, -1);
};

const fit = (ring) => {
  let points = ring;
  // Loosen the tolerance until the outline is editable, rather than picking one
  // tolerance that is wrong for both a small depot and a large works.
  for (let t = 0.00005; points.length > 80 && t < 0.01; t *= 1.6) points = simplify(ring, t);
  return points.slice(0, 500);
};

// The boundary usually exists in OpenStreetMap even when its name does not match what
// was typed, so look it up by position rather than by name. This is what turns a Google
// search result into a real footprint instead of a circle.
const footprintNear = async (lat, lon) => {
  // Any named site, not only an industrial one. Asking for industrial land alone could never see a
  // residential estate, a depot inside a retail park or a hospital campus, so those searches fell
  // through to whatever factory happened to be nearby. Buildings are deliberately excluded - a
  // single tower inside an estate would win as the smallest enclosing shape - and so are
  // administrative boundaries, which describe a ward rather than a site.
  const siteTags = ['landuse', 'amenity', 'leisure', 'place'];
  const near = (tag) => `way(around:1500,${lat},${lon})["name"]["${tag}"];relation(around:1500,${lat},${lon})["name"]["${tag}"];`;
  const query = `[out:json][timeout:25];(${siteTags.map(near).join('')}way(around:1500,${lat},${lon})["name"]["man_made"="works"];relation(around:1500,${lat},${lon})["name"]["man_made"="works"];);out geom;`;
  const response = await fetch('https://overpass-api.de/api/interpreter', { method: 'POST', body: `data=${encodeURIComponent(query)}` });
  if (!response.ok) throw new Error('Boundary lookup failed.');
  const { elements = [] } = await response.json();
  const rings = elements.flatMap((e) => {
    if (e.type === 'way' && e.geometry) return [e.geometry.map((p) => ({ lat: p.lat, lon: p.lon }))];
    // Relations model multipolygons; the longest outer way is a faithful enough outline
    // for a site boundary the administrator is going to adjust anyway.
    return (e.members || []).filter((m) => m.role === 'outer' && m.geometry)
      .map((m) => m.geometry.map((p) => ({ lat: p.lat, lon: p.lon })));
  }).filter((r) => r.length >= 3);
  // Only a boundary that actually contains the point. Taking the largest industrial site merely
  // near it put a search for a residential estate onto the aluminium works across the road: land
  // within a kilometre and a half is not evidence that it is the place that was asked for.
  const enclosing = rings.filter((r) => contains(r, lat, lon));
  // Requiring the point to fall inside was too strict on its own. A geocoder often places its
  // label on the gate, on the approach road, or at the postal address rather than within the
  // fence, and the site was then reported as not found at all. So when nothing encloses the point,
  // the nearest outline whose centre is within four hundred metres is offered instead - close
  // enough to be the same place, and far short of the kilometre and a half that used to let the
  // works across the road win.
  const candidates = enclosing.length ? enclosing : rings
    .map((r) => {
      const middle = r.reduce((sum, p) => ({ lat: sum.lat + p.lat / r.length, lon: sum.lon + p.lon / r.length }), { lat: 0, lon: 0 });
      return { ring: r, away: Math.hypot((middle.lat - lat) * 111320, (middle.lon - lon) * 111320 * Math.cos((lat * Math.PI) / 180)) };
    })
    .filter((x) => x.away < 400)
    .sort((a, b) => a.away - b.away)
    .slice(0, 1)
    .map((x) => x.ring);
  if (!candidates.length) return null;
  // The smallest enclosing boundary is the most specific one: a works inside an industrial estate
  // is the site meant, not the estate around it.
  return fit(candidates.sort((a, b) => area2(a) - area2(b))[0]);
};

// Both search providers publish a bounding box for what they found. Used as it is given, it is
// the rectangle that actually contains the site; rebuilt from a radius it is not, because a square
// whose side is the box's diagonal covers about twice the ground. Corners are [lat, lon] pairs in
// the order the editor draws them.
const rectFrom = (bounds) => (bounds ? [
  { lat: bounds.north, lon: bounds.west }, { lat: bounds.north, lon: bounds.east },
  { lat: bounds.south, lon: bounds.east }, { lat: bounds.south, lon: bounds.west },
] : null);

// Half the shorter side, so switching the area to a circle keeps it inside the site rather than
// swallowing the neighbourhood.
const radiusWithin = (bounds) => (bounds
  ? Math.round(Math.min(metres(bounds.north, bounds.west, bounds.south, bounds.west), metres(bounds.north, bounds.west, bounds.north, bounds.east)) / 2)
  : DEFAULT_RADIUS);

// A last resort that is still a polygon, not a circle: a box the administrator can drag
// into shape by its corners.
const boxAround = (lat, lon, radiusMeters) => {
  const dLat = radiusMeters / 111320;
  const dLon = radiusMeters / (111320 * Math.cos((lat * Math.PI) / 180));
  return [
    { lat: lat + dLat, lon: lon - dLon }, { lat: lat + dLat, lon: lon + dLon },
    { lat: lat - dLat, lon: lon + dLon }, { lat: lat - dLat, lon: lon - dLon },
  ];
};

// Nominatim returns the real premises outline for many industrial sites. Prefer it over
// a guessed circle: a factory boundary is what the administrator actually wants.
const ringFrom = (result) => {
  const g = result.geojson;
  if (!g) return null;
  const ring = g.type === 'Polygon' ? g.coordinates[0]
    : g.type === 'MultiPolygon' ? g.coordinates.map((poly) => poly[0]).sort((a, b) => b.length - a.length)[0]
      : null;
  if (!ring || ring.length < 3) return null;
  // GeoJSON is [lon, lat]; the closing point repeats the first and is dropped.
  const points = ring.slice(0, -1).map(([lon, lat]) => ({ lat, lon }));
  return points.length >= 3 && points.length <= 500 ? points : null;
};

// Google finds almost any site by name and publishes no footprint for it; OpenStreetMap holds the
// footprint but will not always answer to the name that was typed. So when a Google result is
// chosen, its name is put back to OpenStreetMap at those coordinates: the estate that is mapped
// there is the outline the administrator wanted, rather than the box Google drew around a pin.
const outlineByName = async (name, lat, lon) => {
  if (!name || name.trim().length < 3) return null;
  const url = `https://nominatim.openstreetmap.org/search?${new URLSearchParams({ q: name, format: 'jsonv2', polygon_geojson: '1', limit: '5' })}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) return null;
  const candidates = (await response.json())
    .map((r) => ({ ring: ringFrom(r), latitude: Number(r.lat), longitude: Number(r.lon) }))
    .filter((c) => c.ring && Number.isFinite(c.latitude) && metres(lat, lon, c.latitude, c.longitude) < 2000);
  if (!candidates.length) return null;
  // One that actually encloses the point beats one that merely sits near it.
  const enclosing = candidates.filter((c) => contains(c.ring, lat, lon));
  const best = (enclosing.length ? enclosing : candidates).sort((a, b) => metres(lat, lon, a.latitude, a.longitude) - metres(lat, lon, b.latitude, b.longitude))[0];
  return fit(best.ring);
};

const AreaPanel = () => {
  const { data, refresh } = useOperations();
  const [config, setConfig] = useState(null);
  const [query, setQuery] = useState(''); const [results, setResults] = useState(null); const [searching, setSearching] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null); const [notice, setNotice] = useState(null); const [pending, setPending] = useState(false);

  const host = useRef(null); const mapRef = useRef(null); const api = useRef(null); const shape = useRef(null); const overlays = useRef([]);
  const [ready, setReady] = useState(false);

  useEffect(() => { request('/api/operations/maps').then(setConfig).catch(() => setError('Map configuration is unavailable.')); }, []);

  useEffect(() => {
    if (!config?.googleMapsApiKey) return undefined;
    let cancelled = false;
    loadGoogle(config.googleMapsApiKey).then(async (maps) => {
      const { Map } = await maps.importLibrary('maps');
      if (cancelled) return;
      api.current = maps;
      mapRef.current = new Map(host.current, {
        center: { lat: 20.59, lng: 78.96 }, zoom: 4, mapId: config.googleMapId || 'DEMO_MAP_ID', mapTypeId: 'hybrid', mapTypeControl: false, streetViewControl: false, gestureHandling: 'greedy',
      });
      setReady(true);
    }).catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [config]);

  const clearShape = () => { shape.current?.setMap(null); shape.current = null; };

  // One editable overlay at a time. Google gives drag-to-move and vertex/radius handles
  // for free, which is the whole "administrator adjusts it" requirement.
  const drawDraft = useCallback((next) => {
    if (!ready) return;
    clearShape();
    const maps = api.current;
    if (next.points?.length >= 3) {
      shape.current = new maps.Polygon({
        map: mapRef.current, paths: next.points.map((p) => ({ lat: p.lat, lng: p.lon })), editable: true, draggable: true, strokeColor: '#0069ed', fillColor: '#0069ed', fillOpacity: 0.18, strokeWeight: 2,
      });
      // A published outline is often wider than the site being fenced - a company's whole leasehold
      // rather than its works - so corners have to come off as well as move. Google hands vertex
      // dragging and midpoint insertion over for free but no way to remove a point, which leaves a
      // boundary that can only ever grow. Right-clicking a corner deletes it, down to the three a
      // polygon needs.
      shape.current.addListener('rightclick', (event) => {
        const path = shape.current.getPath();
        if (event.vertex == null || path.getLength() <= 3) return;
        path.removeAt(event.vertex);
      });
      const bounds = new maps.LatLngBounds();
      next.points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lon }));
      mapRef.current.fitBounds(bounds, 60);
    } else {
      shape.current = new maps.Circle({
        map: mapRef.current, center: { lat: next.latitude, lng: next.longitude }, radius: next.radiusMeters || DEFAULT_RADIUS, editable: true, draggable: true, strokeColor: '#0069ed', fillColor: '#0069ed', fillOpacity: 0.18, strokeWeight: 2,
      });
      mapRef.current.fitBounds(shape.current.getBounds(), 60);
    }
  }, [ready]);

  // Existing areas stay visible but inert, so a new one can be placed relative to them.
  useEffect(() => {
    if (!ready) return;
    overlays.current.forEach((o) => o.setMap(null));
    const maps = api.current;
    overlays.current = (data.areas || []).filter((a) => a.id !== draft?.id).map((a) => {
      const colour = AREA_COLOUR[a.type] || AREA_COLOUR.PLANT;
      const style = {
        map: mapRef.current, clickable: false, strokeColor: colour, fillColor: colour, fillOpacity: 0.10, strokeWeight: 2,
      };
      return a.shape === 'POLYGON'
        ? new maps.Polygon({ ...style, paths: (a.points || []).map((p) => ({ lat: p.lat, lng: p.lon })) })
        : new maps.Circle({ ...style, center: { lat: a.latitude, lng: a.longitude }, radius: a.radiusMeters });
    });
  }, [ready, data.areas, draft?.id]);

  // Always ends up with a polygon: the published outline if the search carried one, the
  // mapped footprint at those coordinates if not, and only then a draggable box.
  const place = useCallback(async (seed, label) => {
    setResults(null); setError(null); setNotice(null);
    let { points } = seed; let origin = label;
    if (!points) {
      setNotice('Looking up the site boundary…');
      try {
        points = await footprintNear(seed.latitude, seed.longitude);
        origin = points ? `Boundary found on the map (${points.length} points) and simplified for editing.` : null;
      } catch { points = null; origin = null; }
    }
    if (!points) {
      try {
        points = await outlineByName(seed.name, seed.latitude, seed.longitude);
        if (points) origin = `Outline published for this site (${points.length} points), matched by name at these coordinates.`;
      } catch { points = null; }
    }
    if (!points && seed.bounds) {
      points = rectFrom(seed.bounds);
      origin = 'No mapped outline here, so the published bounding box was used. Drag its corners to trace the site.';
    }
    if (!points) {
      points = boxAround(seed.latitude, seed.longitude, seed.radiusMeters || DEFAULT_RADIUS);
      origin = 'No mapped boundary here, so a box was placed. Drag its corners to trace the site.';
    }
    const next = {
      id: null, name: (seed.name || '').slice(0, 120), type: 'PLANT', latitude: seed.latitude, longitude: seed.longitude, radiusMeters: seed.radiusMeters || DEFAULT_RADIUS, points,
    };
    setDraft(next); setNotice(origin); drawDraft(next);
  }, [drawDraft]);

  // Clicking the map is the "pick a point" path: it resolves to the site boundary there.
  useEffect(() => {
    if (!ready) return undefined;
    const listener = mapRef.current.addListener('click', (e) => {
      place({
        name: '', latitude: e.latLng.lat(), longitude: e.latLng.lng(), radiusMeters: DEFAULT_RADIUS, points: null,
      }, null);
    });
    return () => listener.remove();
  }, [ready, place]);

  // Two sources, because neither alone is enough. OpenStreetMap publishes the real
  // premises outline for many plants but simply does not have others; Google finds
  // almost any site by name but exposes only a rectangular viewport, never a footprint.
  // OpenStreetMap matches the name it holds, token for token, and the names people type carry the
  // company's legal form while the map does not. "TATA Steel Ltd Meramandali" finds nothing;
  // "TATA Steel Meramandali" finds the plant and its five-kilometre footprint. So the words that
  // belong to a registrar rather than to a place are dropped and the search tried again.
  const LEGAL_FORM = /\b(ltd|limited|pvt|private|inc|incorporated|corp|corporation|co|llp|plc|gmbh|sa|bv)\b\.?/gi;
  const ask = async (text) => {
    const url = `https://nominatim.openstreetmap.org/search?${new URLSearchParams({ q: text, format: 'jsonv2', polygon_geojson: '1', limit: '5', addressdetails: '1' })}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('OpenStreetMap search failed.');
    return response.json();
  };

  const searchOsm = async () => {
    let rows = await ask(query);
    const plainer = query.replace(LEGAL_FORM, ' ').replace(/\s+/g, ' ').trim();
    if (!rows.length && plainer && plainer !== query) rows = await ask(plainer);
    return rows.map((r) => {
      const box = (r.boundingbox || []).map(Number);
      const bounds = box.length === 4 && box.every(Number.isFinite) ? { south: box[0], north: box[1], west: box[2], east: box[3] } : null;
      return {
        key: `osm-${r.osm_type}-${r.osm_id}`,
        source: 'OpenStreetMap',
        label: r.display_name,
        latitude: Number(r.lat),
        longitude: Number(r.lon),
        name: (r.name || r.display_name || '').split(',')[0],
        points: ringFrom(r),
        bounds,
        radiusMeters: radiusWithin(bounds),
      };
    });
  };

  const searchGoogle = async () => {
    if (!api.current) return [];
    const { Place } = await api.current.importLibrary('places');
    const { places } = await Place.searchByText({ textQuery: query, fields: ['displayName', 'formattedAddress', 'location', 'viewport'], maxResultCount: 5 });
    return (places || []).map((place) => {
      const v = place.viewport;
      // The viewport is the bounding box Google holds for the place, so it is carried through as
      // corners. It used to be reduced to half its diagonal and rebuilt as a square, which drew a
      // box about twice the size of the one Google actually returned.
      const bounds = v ? { north: v.getNorthEast().lat(), east: v.getNorthEast().lng(), south: v.getSouthWest().lat(), west: v.getSouthWest().lng() } : null;
      return {
        key: `g-${place.id || place.displayName}`,
        source: 'Google',
        label: `${place.displayName}${place.formattedAddress ? ` — ${place.formattedAddress}` : ''}`,
        latitude: place.location.lat(),
        longitude: place.location.lng(),
        name: place.displayName,
        points: null,
        bounds,
        radiusMeters: Math.min(Math.max(radiusWithin(bounds), 50), 20000),
      };
    });
  };

  const search = async (event) => {
    event.preventDefault();
    if (query.trim().length < 3) { setError('Enter at least three characters to search.'); return; }
    setSearching(true); setError(null); setResults(null);
    const [google, osm] = await Promise.allSettled([searchGoogle(), searchOsm()]);
    const rank = (r) => (r.points ? 0 : r.bounds ? 1 : 2);
    const found = [...(google.value || []), ...(osm.value || [])]
      .map((r, index) => ({ r, index })).sort((a, b) => rank(a.r) - rank(b.r) || a.index - b.index).map((x) => x.r);
    setResults(found);
    if (!found.length) {
      setError(google.status === 'rejected' && osm.status === 'rejected'
        ? 'Place search is unavailable. You can still click the map to place a boundary.'
        : 'No places matched that search. Try a shorter name, or click the map to place a boundary.');
    } else if (google.status === 'rejected' && osm.value?.length) {
      // Usually means the Places API is not enabled on the key — worth saying plainly
      // rather than silently returning fewer results.
      setNotice('Showing OpenStreetMap results only; Google place search did not respond.');
    }
    setSearching(false);
  };

  const choose = (result) => place(result, result.points ? `Published outline used (${result.points.length} points). Drag the handles to adjust it.` : null);

  const edit = (area) => {
    const next = {
      id: area.id, name: area.name, type: area.type, latitude: area.latitude, longitude: area.longitude, radiusMeters: area.radiusMeters || DEFAULT_RADIUS, points: area.shape === 'POLYGON' ? area.points : null,
    };
    setDraft(next); setError(null); setNotice(null); drawDraft(next);
  };

  // Read the geometry back off the overlay rather than from state: the administrator's
  // drags happen on the map, and only the overlay knows where things ended up.
  const geometry = () => {
    if (!shape.current) return null;
    if (shape.current.getPath) {
      return { points: shape.current.getPath().getArray().map((p) => ({ lat: p.lat(), lon: p.lng() })) };
    }
    const c = shape.current.getCenter();
    return { latitude: c.lat(), longitude: c.lng(), radiusMeters: Math.round(shape.current.getRadius()) };
  };

  const save = async () => {
    if (!draft?.name.trim()) { setError('Give the area a name.'); return; }
    const geo = geometry();
    if (!geo) { setError('Place the area on the map first.'); return; }
    setPending(true); setError(null);
    try {
      const body = { name: draft.name.trim(), type: draft.type, ...geo };
      if (draft.id) await request(`/api/operations/areas/${draft.id}`, 'PATCH', body);
      else await request('/api/operations/areas', 'POST', body);
      setNotice(`${body.name} saved.`); setDraft(null); clearShape(); await refresh();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const remove = async (area) => {
    setPending(true); setError(null);
    try {
      await request(`/api/operations/areas/${area.id}`, 'DELETE');
      setNotice(`${area.name} deleted.`);
      if (draft?.id === area.id) { setDraft(null); clearShape(); }
      await refresh();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const areas = data.areas || [];
  const vehicleName = (id) => (data.vehicles || []).find((v) => v.id === id)?.name || `Vehicle ${id}`;

  return (
    <Stack gap={2}>
      <Typography color="text.secondary">
        Search for a plant or depot to pull in its published outline, or click the map to drop a boundary. Drag a handle to move a corner, drag the faint one between two corners to add another, and right-click a corner to remove it.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <Paper component="form" onSubmit={search} variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" gap={2}>
          <TextField fullWidth size="small" label="Find a factory, depot or address" value={query} onChange={(e) => setQuery(e.target.value)} />
          <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={searching}>Search</Button>
        </Stack>
        {results?.length > 0 && (
          <List dense sx={{ mt: 1 }}>
            {results.map((r) => (
              <ListItemButton key={r.key} onClick={() => choose(r)}>
                <ListItemText
                  primary={r.label}
                  secondary={r.points ? `${r.source} · real outline (${r.points.length} points)` : r.bounds ? `${r.source} · bounding box, about ${r.radiusMeters * 2} m across` : `${r.source} · point, ${r.radiusMeters} m default`}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Paper>

      {draft && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack gap={2}>
            <Typography variant="subtitle1">{draft.id ? 'Edit area' : 'New area'}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField fullWidth size="small" label="Area name" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} inputProps={{ maxLength: 120 }} />
              <TextField select fullWidth size="small" label="Area type" value={draft.type} onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}>
                {TYPES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
            </Stack>
            <Typography variant="caption" color="text.secondary">{TYPES.find(([v]) => v === draft.type)?.[2]}</Typography>
            <Stack direction="row" gap={2}>
              <Button variant="contained" onClick={save} disabled={pending}>{draft.id ? 'Save changes' : 'Create area'}</Button>
              <Button onClick={() => { setDraft(null); clearShape(); }} disabled={pending}>Cancel</Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Box sx={{
        height: 460, position: 'relative', bgcolor: (t) => (t.palette.mode === 'dark' ? '#1B2733' : '#e5edf3'), borderRadius: 2, overflow: 'hidden',
      }}
      >
        <Box ref={host} sx={{ position: 'absolute', inset: 0 }} />
        {!ready && !error && <Alert severity="info" sx={{ position: 'absolute', bottom: 12, left: 12 }}>Loading map…</Alert>}
      </Box>

      <Paper variant="outlined">
        <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>{`Areas (${areas.length})`}</Typography>
        {areas.length === 0 ? (
          <Typography color="text.secondary" sx={{ px: 2, pb: 2 }}>No areas yet. Search for a site above, or click the map to place one.</Typography>
        ) : (
          <List dense>
            {areas.map((a) => (
              <ListItemButton key={a.id} onClick={() => edit(a)} selected={draft?.id === a.id}>
                <ListItemText
                  primary={(
                    <Stack direction="row" gap={1} alignItems="center">
                      <span>{a.name}</span>
                      <Chip size="small" label={areaTypeLabel(a.type)} color={a.type === 'PARKING' ? 'warning' : a.type === 'WAREHOUSE' ? 'info' : 'success'} variant="outlined" />
                      <Chip size="small" label={a.shape === 'POLYGON' ? `${(a.points || []).length}-point outline` : `${a.radiusMeters} m radius`} variant="outlined" />
                    </Stack>
                  )}
                  secondary={(a.occupantVehicleIds || []).length
                    ? `${a.type === 'PARKING' ? 'Waiting to be called in' : a.type === 'WAREHOUSE' ? 'At this site' : 'On site'}: ${a.occupantVehicleIds.map(vehicleName).join(', ')}`
                    : 'No vehicles inside right now'}
                />
                <Tooltip title="Delete area">
                  <IconButton edge="end" color="error" onClick={(e) => { e.stopPropagation(); remove(a); }} disabled={pending}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Tooltip>
              </ListItemButton>
            ))}
          </List>
        )}
      </Paper>
      <Typography variant="caption" color="text.secondary">
        Search covers Google Places and OpenStreetMap. Only OpenStreetMap publishes real site outlines; Google results are placed as a circle you resize.
      </Typography>
    </Stack>
  );
};

export default AreaPanel;
